// https://zeroheight.com/5ffcc47c9/p/5258e9-universal-color-palette/b/37ef06
export const COLOR = {
  // Neutral
  white: '#FFFFFF',
  neutral0: '#F5F6F7',
  neutral5: '#EDEEF2',
  neutral10: '#D9DBDE',
  neutral20: '#C2C4C7',
  neutral30: '#A6A9AD',
  neutral40: '#8E9194',
  neutral50: '#757678',
  neutral60: '#656667',
  neutral70: '#4B4C4D',
  neutral80: '#3D3F40',
  neutral90: '#2A2B2B',
  neutral95: '#212021',
  neutral100: '#202121',
  black: '#000000',
  // Blue
  blue0: '#F5F8FF',
  blue30: '#4383FA',
  blue60: '#004ACE',
  blue70: '#0036B5',
  blue80: '#00259E',
  blue100: '#050d27',
  // Green
  green5: '#DCF2E0',
  green40: '#10d669',
  green50: '#0dc45f',
  green60: '#09b054',
  green70: '#059646',
  green80: '#04802D',
  green90: '#005922',
  green100: '#00260B',
  // Orange
  orange30: '#F2903F',
  orange40: '#F08024',
  orange50: '#D9690D',
  orange60: '#BF5804',
  orange70: '#A34905',
  // Red
  red20: '#F78D8D',
  red30: '#F94343',
  red50: '#E10500',
  red60: '#CC0E00',
  red70: '#AD1100',
  // Yellow
  yellow5: '#FAF7EB',
  yellow20: '#F2DD8A',
  yellow30: '#F2CE3F',
  yellow40: '#F2BE2C',
  yellow50: '#EBAA17',
  yellow60: '#CC9618',
  yellow80: '#805804',
  yellow90: '#593C00',
  yellow100: '#2B1C00'
};

const mpRed = COLOR.red50;
const mpRedDark = COLOR.red70;

// https://zeroheight.com/50d551c87/p/40ecda-welcome/b/047b74
const THEME = {
  school: {
    primary: mpRed,
    primaryDark: mpRedDark
  },
  nav: {
    isSiteNavDark: false,
    header: mpRed,
    headerDark: mpRedDark,
    footer: mpRed,
    footerDark: mpRedDark
  },
  text: {
    primary: COLOR.neutral95,
    secondary: COLOR.neutral60,
    tertiary: COLOR.neutral50,
    disabled: COLOR.neutral30,
    selected: COLOR.neutral95,
    unselected: COLOR.neutral60,
    positive: COLOR.green80,
    positiveHover: COLOR.green90,
    negative: COLOR.red60,
    negativeHover: mpRedDark,
    information: COLOR.yellow80,
    informationHover: COLOR.yellow90,
    warning: COLOR.orange60,
    warningHover: COLOR.orange70,
    alert: COLOR.red60
  },
  surface: {
    primary: COLOR.white,
    secondary: COLOR.neutral5,
    tertiary: COLOR.neutral0,
    brand: mpRed,
    positive: COLOR.green5,
    information: COLOR.yellow5
  },
  surfaceDark: {
    primary: COLOR.neutral95,
    secondary: COLOR.black,
    tertiary: COLOR.neutral90
  },
  // Utility > General
  general: {
    border: COLOR.neutral10,
    borderHover: mpRed,
    disabled: COLOR.neutral30,
    uiBackground: COLOR.white,
    uiBackgroundDisabled: COLOR.neutral0,
    uiBackgroundHover: COLOR.neutral0,
    uiElementActive: mpRed,
    iconPrimary: mpRed,
    iconSecondary: COLOR.neutral80,
    modalBackground: COLOR.neutral100 // TODO 40%
  },
  // Utility > Status > Graphic
  status: {
    positive: COLOR.green70,
    positiveHover: COLOR.green80,
    negative: COLOR.red60,
    negativeHover: mpRedDark,
    information: COLOR.yellow50,
    informationHover: COLOR.yellow60,
    warning: COLOR.orange50,
    warningHover: COLOR.orange60,
    alert: COLOR.red60
  },
  brand: {
    maxpreps: mpRed,
    maxprepsDark: mpRedDark,
    cbsSports: COLOR.blue60,
    sports247: COLOR.blue80
  },
  // https://zeroheight.com/5ffcc47c9/p/81f127-third-party-colors/b/03aad1
  social: {
    facebook: '#1877f2',
    facebookHover: '#0c64d6',
    twitter: '#1da1f2',
    twitterHover: '#0c8bd9',
    instagram: '#c32aa3',
    instagramHover: '#a5238a',
    hudl: '#ff5501',
    gameChanger: '#108073'
  },
  // Unofficial - Should be integrated
  other: {
    icon: COLOR.neutral60,
    iconLight: COLOR.neutral10,
    link: COLOR.blue60,
    deleted: '#ffedef'
  },
  shadow: {
    light: 'rgba(0,0,0,0.15)',
    medium: 'rgba(0,0,0,0.25)',
    dark: 'rgba(0,0,0,0.5)'
  }
};

export const DARK_THEME = {
  ...THEME,
  nav: {
    isSiteNavDark: false,
    header: mpRed,
    headerDark: mpRedDark,
    footer: COLOR.black,
    footerDark: mpRedDark
  },
  text: {
    primary: COLOR.white,
    secondary: COLOR.neutral20,
    tertiary: COLOR.neutral40,
    disabled: COLOR.neutral60,
    selected: COLOR.white,
    unselected: COLOR.neutral40,
    positive: COLOR.green50,
    positiveHover: COLOR.green40,
    negative: COLOR.red30,
    negativeHover: COLOR.red20,
    information: COLOR.yellow40,
    informationHover: COLOR.yellow30,
    warning: COLOR.orange40,
    warningHover: COLOR.orange30,
    alert: COLOR.red30
  },
  surface: {
    primary: COLOR.neutral95,
    secondary: COLOR.black,
    tertiary: COLOR.neutral90,
    brand: COLOR.red50,
    positive: COLOR.green100,
    information: COLOR.yellow100
  },
  // Utility > General
  general: {
    border: COLOR.neutral80,
    borderHover: COLOR.red50,
    disabled: COLOR.neutral60,
    uiBackground: COLOR.neutral100,
    uiBackgroundDisabled: COLOR.neutral95,
    uiBackgroundHover: COLOR.neutral90,
    uiElementActive: COLOR.red50,
    iconPrimary: COLOR.red50,
    iconSecondary: COLOR.neutral20
  },
  // Utility > Status > Graphic
  status: {
    positive: COLOR.green50,
    positiveHover: COLOR.green40,
    negative: COLOR.red30,
    negativeHover: COLOR.red20,
    information: COLOR.yellow30,
    informationHover: COLOR.yellow20,
    warning: COLOR.orange40,
    warningHover: COLOR.orange30,
    alert: COLOR.red30
  },
  brand: {
    maxpreps: COLOR.red50,
    cbsSports: COLOR.blue80,
    sports247: COLOR.blue60
  }
};

export default THEME;
